import { Injectable } from '@angular/core';
import { InterceptorSkipHeader } from '../_constants/authConstants';
import { HttpHeaders, HttpClient, HttpHandler, HttpRequest, HttpEvent } from '@angular/common/http';
import { Observable, BehaviorSubject, throwError } from 'rxjs';
import { catchError, switchMap, filter, take } from 'rxjs/operators';
import { AuthenticationService } from '../_services/auth.service';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class TokenService {
  private refreshTokenInProgress = false;
  private refreshTokenSubject = new BehaviorSubject<string | null>(null);
  private refreshCompletedSubject = new BehaviorSubject<boolean>(false);
  public refreshCompleted$ = this.refreshCompletedSubject.asObservable();

  constructor(private http: HttpClient, private authService: AuthenticationService) { }

  public injectToken(request: HttpRequest<any>): HttpRequest<any> {
    const token = this.getAccessToken();
    if (token) {
      return request.clone({
        setHeaders: {
          Authorization: `Bearer ${token}`
        },
        withCredentials: true
      });
    }
    return request;
  }

  private getAccessToken(): string | null {
    return localStorage.getItem('token');
  }

  public handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.refreshTokenInProgress) {
      this.refreshTokenInProgress = true;
      this.refreshTokenSubject.next(null);
      this.refreshCompletedSubject.next(false); // Reset refresh status

      return this.getNewToken().pipe(
        switchMap((response: any) => {
          const accessToken = response?.accessToken;
          this.saveToken(accessToken);
          this.refreshTokenInProgress = false;
          this.refreshTokenSubject.next(accessToken);
          this.refreshCompletedSubject.next(true); // Notify completion
          return next.handle(this.injectToken(request));
        }),
        catchError(err => {
          this.refreshTokenInProgress = false;
          this.refreshCompletedSubject.next(true); // Allow app to continue
          // Any other request that hit a 401 while this refresh was in
          // flight is parked in the `else` branch below, waiting on this
          // same subject for a non-null token. A BehaviorSubject that never
          // emits again just leaves them hanging forever — erroring it
          // wakes every waiter with a real (catchable) error instead, then
          // this gets replaced so the NEXT 401 starts with a clean subject
          // (an errored Subject rejects every future subscriber immediately).
          this.refreshTokenSubject.error(err);
          this.refreshTokenSubject = new BehaviorSubject<string | null>(null);
          this.authService.logout();
          return throwError(() => err);
        })
      );
    } else {
      // Wait for refresh to complete and then retry
      return this.refreshTokenSubject.pipe(
        filter(token => token != null),
        take(1),
        switchMap(token => next.handle(this.injectToken(request)))
      );
    }
  }

  getNewToken() {
    const headers = new HttpHeaders().set(InterceptorSkipHeader, '');
    return this.http.post<any>(
      environment.authServerRefreshApi,
      {},
      {
        headers: headers,
        withCredentials: true
      }
    );
  }
  
  saveToken(tokenData:any) {
    localStorage.setItem('token', tokenData);
  }
}